import {
  Breakpoints,
  CTA,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
  styled,
  DesignTokenSizeEnum
} from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import useAppNavigate from '@/hooks/useAppNavigate';
import useGlobalStore from '@/stores/globalStore';
import Box from '../components/Box/Box';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { getFirstBuildableConfiguration } from '../utils/storeUtils';
import { convertBreakpointsToMediaQueries } from '../utils/themeUtils';
import { performVWBasicMultipleVehiclesButtonClick } from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { NAVIGATION_HEIGHT } from '../constants/navigation';

const StyledContainer = styled.div(
  ({ theme }) => `
  padding:${theme.size.static450} ${theme.size.static400};
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    ${theme.colors.border.divider} 55%
  );

  @media (min-width: ${Breakpoints.b960}px) {
    min-height: calc(100vh - ${NAVIGATION_HEIGHT}px);
  }

  @media (min-width: ${Breakpoints.b1280}px) {
    padding:${theme.size.static530} ${theme.size.static1000};
    strong {
      display: block;
    }
  }
`
);

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${convertBreakpointsToMediaQueries({
    b1280: {
      flexDirection: 'row',
    },
  })}
`;

const StyledContent = styled.div(
  ({ theme }) => `
  display: grid;
  margin:${theme.size.dynamic0130} 0;

  ${convertBreakpointsToMediaQueries({
    b1280: {
      gap: theme.size.static530,
      gridAutoFlow: 'column',
      gridAutoColumns: '1fr',
    },
  })}
`
);

interface GetTermsProps {
  text: string;
  margin?: boolean;
  inherit?: true;
}

export const getTerms = ({ text, inherit, margin = true }: GetTermsProps) => (
  <Box
    as={Text}
    tag={TextTag.div}
    color={inherit ? TokenTextColor.inherit : TokenTextColor.primary}
    appearance={TokenTextAppearance.copy100}
    mb={margin ? 'static350' : 'static0'}
  >
    {text}
  </Box>
);

const Terms = () => {
  const { retailers, configurations } = useGlobalStore((state) => state.app);

  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();

  const navigate = useAppNavigate();

  const firstBuildableConfigurationUrl =
    getFirstBuildableConfiguration(configurations).fullUrl;
  const ctaLabel = contentManager.get('ctas-view-pb');

  const handleClick = (contentId: string) => {
    performVWBasicMultipleVehiclesButtonClick({
      label: ctaLabel,
      sectionName: SectionNames.TERMS_SCREEN,
      configurations,
      retailers,
      analyticsManager,
      analyticsBuilder,
      contentId,
    });
    navigate(firstBuildableConfigurationUrl);
  };

  const handleCtaTopClick = () => {
    handleClick("cta-view-brochure-top");
  };
  const handleCtaBottomClick = () => {
    handleClick("cta-view-brochure-bottom");
  };



  return (
    <StyledContainer>
      <SectionHeader
        titleSize={TokenTextAppearance.headline500}
        title={contentManager.get('understanding-pb-title-normal')}
        titleBold={contentManager.get('understanding-pb-title-bold')}
        type="boldLight"
      />
      <Box mb={DesignTokenSizeEnum.dynamic0130}></Box>
      <ButtonContainer>
        <CTA tag="button" onClick={handleCtaTopClick}>
          {ctaLabel}
        </CTA>
      </ButtonContainer>
      <StyledContent>
        <Box>
          {getTerms({ text: contentManager.get('understanding-pb-text') })}
          {getTerms({ text: contentManager.get('understanding-pb-section-2') })}
          {getTerms({
            text: contentManager.get('understanding-pb-section-3'),
            margin: false,
          })}
        </Box>

        <Box>
          {getTerms({ text: contentManager.get('understanding-pb-section-4') })}
          {getTerms({ text: contentManager.get('understanding-pb-section-5') })}
          {getTerms({ text: contentManager.get('understanding-pb-section-6') })}
          {getTerms({ text: contentManager.get('understanding-pb-section-7') })}
          {getTerms({
            text: contentManager.get('understanding-pb-section-8'),
            margin: false,
          })}
        </Box>
      </StyledContent>

      <ButtonContainer>
        <CTA tag="button" onClick={handleCtaBottomClick}>
          {ctaLabel}
        </CTA>
      </ButtonContainer>
    </StyledContainer>
  );
};

export default Terms;
